import React, { forwardRef, useMemo, useRef } from "react";
import mergeRefs from "merge-refs";
import { useIntl } from "react-intl";
import { useDialog } from "@/hook";


interface FileInputProps extends React.ComponentProps<"input"> {
    onUpload: (files: File[]) => void | Promise<void>;
}

const FileInput = forwardRef<HTMLInputElement, FileInputProps>((props, propsRef) => {
    const { onUpload, ...rest } = props;

    const acceptedTypes = useMemo(() => {
        return props.accept?.split(',').map(type => type.trim().toLowerCase()) ?? [];
    }, [props.accept]);

    const intl = useIntl();
    const dialog = useDialog();

    const inputRef = useRef<HTMLInputElement>(null);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);

        const invalidFile = files.find(file => !isFileAccepted( file, acceptedTypes ));
        if (invalidFile) {
            dialog.open({
                variant: "danger",
                title: intl.formatMessage({ id: "i00035" }),
                content: intl.formatMessage({ id: "i00036" }, { fileName: invalidFile.name }),
            });
            return;
        }

        onUpload(files);
        props.onChange?.(e);
    };

    const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
        if (!inputRef.current) {
            return;
        }
        inputRef.current.value = "";
        props.onClick?.(e);
    };

    return (
        <input ref={mergeRefs(inputRef, propsRef)}
               type="file"
               className="hidden"
               aria-hidden="true"
               tabIndex={-1}
               onClick={onClick}
               onChange={onChange}
               {...rest}
        />
    );
});

const isFileAccepted = (file: File, types: string[]): boolean => {
    if (types.length === 0 || types.includes("*/*")) {
        return true;
    }

    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.includes('.')
        ? '.' + fileName.split('.').pop()
        : '';

    return types.some(type => {
        if (type.startsWith('.')) {
            return fileExtension === type;
        }
        if (type.endsWith('/*')) {
            return fileType.startsWith(type.slice(0, -2) + '/');
        }
        return fileType === type;
    });
};

export default FileInput;
